/**
 * M27 Step 2 — Company Structure (PR 3).
 *
 * Per BUILD_READINESS_M27.md §6:
 *   - Customer group display (name read-only + slug + "contact
 *     support to rename" link).
 *   - Legal Entities list reusing the M26.5 LE CRUD endpoints.
 *   - Single-LE customers can toggle "I have one legal entity only"
 *     to hide the multi-LE chrome.
 *   - "Add Legal Entity" inline (no separate modal — reuse the same
 *     fields the /settings/legal-entities surface offers).
 *   - AI helps callout suggesting standard org structures.
 *   - Reads + writes the same /api/legal-entities backend the
 *     Settings page uses.
 *
 * The LE switcher in the top bar was repaired in the same PR
 * (LegalEntitySwitcher.tsx rebuilt as a proper popover).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  ChevronRight,
  ExternalLink,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { AIHelpsHereCallout } from "../../components/onboarding/AIHelpsHereCallout";
import { api, ApiError } from "../../lib/api";
import type {
  LegalEntityCreateBody,
  LegalEntityListItem,
  TenantSettings,
} from "../../lib/api";
import { ONBOARDING_JOURNEY_QUERY_KEY } from "../../lib/onboarding";
import { useOnboardingAutoSave } from "../../lib/use-onboarding-autosave";
import { StepLayout } from "./StepLayout";

export function Step2CompanyReal() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const auto = useOnboardingAutoSave("company");
  const [showAdd, setShowAdd] = useState(false);
  const [singleLEOnly, setSingleLEOnly] = useState(false);

  const tenant = useQuery({
    queryKey: ["tenant-settings"],
    queryFn: () => api.getTenantSettings(),
  });
  const les = useQuery({
    queryKey: ["legal-entities", false, ""],
    queryFn: () => api.listLegalEntities({}),
  });

  const archive = useMutation({
    mutationFn: (id: string) => api.archiveLegalEntity(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["legal-entities"] }),
  });

  // Hydrate singleLEOnly from previously-saved draft so refresh
  // preserves the toggle (commandment 28).
  const stateQ = useQuery({
    queryKey: ONBOARDING_JOURNEY_QUERY_KEY,
    queryFn: () => api.getOnboardingJourneyState(),
  });
  useEffect(() => {
    const flag = stateQ.data?.steps?.company?.data?.single_le_only;
    if (typeof flag === "boolean") setSingleLEOnly(flag);
  }, [stateQ.data]);

  const tenantData = tenant.data as TenantSettings | undefined;
  const allLes = les.data ?? [];
  const visibleLes = singleLEOnly ? allLes.slice(0, 1) : allLes;

  return (
    <StepLayout
      slug="company"
      title={t("app.onb_step_company")}
      subtitle={t("app.s2_subtitle")}
      continueLabel={t("app.s2_continue_to_policies")}
    >
      <div className="flex flex-col gap-5">
        <AIHelpsHereCallout
          message={t("app.s2_ai_msg")}
          detail={t("app.s2_ai_detail")}
        />

        <section className="rounded-xl border border-border-subtle bg-surface-default p-5">
          <header className="mb-3 flex items-center gap-2">
            <Building2 size={16} className="text-brand-blue" aria-hidden />
            <h2 className="text-sm font-semibold text-text-primary">
              {t("app.s2_group_title")}
            </h2>
            <a
              href="mailto:support@solvait.ai?subject=Tenant rename request"
              className="ms-auto inline-flex items-center gap-1 text-xs text-brand hover:underline"
            >
              {t("app.s2_contact_support_rename")}
              <ExternalLink size={10} aria-hidden />
            </a>
          </header>
          {tenantData ? (
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <DlEntry label={t("app.s2_name_en")} value={tenantData.customer_group_name_en} />
              <DlEntry
                label={t("app.s2_name_ar")}
                value={tenantData.customer_group_name_ar}
                dir="rtl"
              />
              <DlEntry
                label={t("app.s2_workspace_url")}
                value={`${tenantData.slug}.solvait.ai`}
                mono
              />
            </dl>
          ) : (
            <p className="text-sm text-text-tertiary">{t("app.s2_loading")}</p>
          )}
        </section>

        <section className="rounded-xl border border-border-subtle bg-surface-default p-4">
          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              checked={singleLEOnly}
              onChange={(e) => {
                setSingleLEOnly(e.target.checked);
                auto.save({ single_le_only: e.target.checked });
              }}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium text-text-primary">
                {t("app.s2_single_le_label")}
              </span>
              <span className="block text-xs text-text-secondary">
                {t("app.s2_single_le_body")}
              </span>
            </span>
          </label>
        </section>

        <section className="rounded-xl border border-border-subtle bg-surface-default p-5">
          <header className="mb-3 flex items-center gap-2">
            <h2 className="text-sm font-semibold text-text-primary">
              {t("app.s2_les_title")}
            </h2>
            <span className="text-xs text-text-tertiary">
              {t("app.s2_n_active", { n: visibleLes.length })}
            </span>
            <Link
              to="/settings/legal-entities"
              className="ms-auto inline-flex items-center gap-1 text-xs text-brand hover:underline"
            >
              {t("app.s2_manage_in_settings")}
            </Link>
          </header>
          {visibleLes.length === 0 ? (
            <p className="text-sm text-text-tertiary">{t("app.s2_loading")}</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {visibleLes.map((le) => (
                <LeRow
                  key={le.id}
                  le={le}
                  canArchive={!singleLEOnly && visibleLes.length > 1}
                  onArchive={() => {
                    if (confirm(t("app.s2_archive_confirm", { name: le.name_en }))) {
                      archive.mutate(le.id);
                    }
                  }}
                />
              ))}
            </ul>
          )}
          {!singleLEOnly && (
            <div className="mt-4">
              {showAdd ? (
                <AddLeInline
                  onCancel={() => setShowAdd(false)}
                  onCreated={() => {
                    qc.invalidateQueries({ queryKey: ["legal-entities"] });
                    setShowAdd(false);
                  }}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setShowAdd(true)}
                  className="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-surface-default px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-sunken"
                >
                  <Plus size={14} aria-hidden /> {t("app.s2_add_le_btn")}
                </button>
              )}
            </div>
          )}
        </section>

        <section className="rounded-xl border border-ai-subtle bg-ai-bg p-4">
          <header className="flex items-center gap-2">
            <Sparkles size={14} className="text-brand-teal" aria-hidden />
            <h2 className="text-sm font-semibold text-text-primary">
              {t("app.s2_common_patterns_title")}
            </h2>
          </header>
          <p className="mt-1 text-xs text-text-secondary">
            {t("app.s2_common_patterns_body")}{" "}
            <Link
              to="/people/positions"
              className="text-brand hover:underline"
            >
              {t("app.s2_people_positions_link")}
            </Link>{" "}
            {t("app.s2_after_this_step")}
          </p>
        </section>
      </div>
    </StepLayout>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function DlEntry({
  label,
  value,
  mono,
  dir,
}: {
  label: string;
  value: string;
  mono?: boolean;
  dir?: "ltr" | "rtl";
}) {
  return (
    <div>
      <dt className="text-xs font-medium text-text-secondary">{label}</dt>
      <dd
        dir={dir}
        className={`mt-1 text-sm text-text-primary ${
          mono ? "font-mono" : ""
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

function LeRow({
  le,
  canArchive,
  onArchive,
}: {
  le: LegalEntityListItem;
  canArchive: boolean;
  onArchive: () => void;
}) {
  const { t } = useTranslation();
  return (
    <li className="flex items-center gap-3 rounded-lg border border-border-subtle bg-surface-default px-3 py-2.5">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-surface-sunken text-text-secondary">
        <Building2 size={14} aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-text-primary">
          {le.name_en}
        </p>
        <p className="truncate text-xs text-text-tertiary" dir="rtl">
          {le.name_ar}
        </p>
      </div>
      <div className="hidden text-xs text-text-tertiary sm:flex sm:flex-col sm:items-end">
        <span>
          {t("app.s2_le_cr_prefix")} <span className="font-mono">{le.cr_number}</span>
        </span>
        <span>
          {t("app.s2_le_summary", { employees: le.employee_count, contracts: le.active_contract_count })}
        </span>
      </div>
      <Link
        to={`/settings/legal-entities`}
        className="inline-flex items-center gap-1 rounded text-xs text-brand hover:underline"
      >
        {t("app.s2_edit")}
        <ChevronRight size={10} aria-hidden className="rtl:rotate-180" />
      </Link>
      {canArchive && (
        <button
          type="button"
          onClick={onArchive}
          aria-label={t("app.s2_archive_aria", { name: le.name_en })}
          className="rounded border border-rose-200 px-1.5 py-0.5 text-xs text-rose-700 hover:bg-rose-50"
        >
          <Trash2 size={12} aria-hidden />
        </button>
      )}
    </li>
  );
}

function AddLeInline({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<LegalEntityCreateBody>({
    name_en: "",
    name_ar: "",
    cr_number: "",
    registered_city: "",
    region: "bahrain",
  });
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.createLegalEntity(form),
    onSuccess: () => onCreated(),
    onError: (e) =>
      setErr(e instanceof ApiError ? e.detail : String(e)),
  });

  return (
    <div className="rounded-lg border border-border-subtle bg-surface-sunken p-3">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <input
          value={form.name_en}
          placeholder={t("app.s2_placeholder_name_en")}
          onChange={(e) => setForm({ ...form, name_en: e.target.value })}
          className="rounded border border-border-subtle bg-surface-default px-2 py-1.5 text-sm"
        />
        <input
          value={form.name_ar}
          placeholder={t("app.s2_placeholder_name_ar")}
          dir="rtl"
          onChange={(e) => setForm({ ...form, name_ar: e.target.value })}
          className="rounded border border-border-subtle bg-surface-default px-2 py-1.5 text-sm text-end"
        />
        <input
          value={form.cr_number}
          placeholder={t("app.s2_placeholder_cr")}
          onChange={(e) => setForm({ ...form, cr_number: e.target.value })}
          className="rounded border border-border-subtle bg-surface-default px-2 py-1.5 font-mono text-sm"
        />
        <input
          value={form.registered_city ?? ""}
          placeholder={t("app.s2_placeholder_city")}
          onChange={(e) =>
            setForm({ ...form, registered_city: e.target.value })
          }
          className="rounded border border-border-subtle bg-surface-default px-2 py-1.5 text-sm"
        />
      </div>
      {err && <p className="mt-2 text-xs text-rose-700">{err}</p>}
      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-border-subtle px-2.5 py-1 text-xs text-text-secondary hover:bg-surface-default"
        >
          {t("common.cancel")}
        </button>
        <button
          type="button"
          onClick={() => {
            setErr(null);
            if (
              !form.name_en.trim() ||
              !form.name_ar.trim() ||
              !form.cr_number.trim()
            ) {
              setErr(t("app.s2_required_msg"));
              return;
            }
            create.mutate();
          }}
          disabled={create.isPending}
          className="rounded bg-brand px-3 py-1 text-xs font-medium text-brand-fg hover:opacity-90 disabled:opacity-50"
        >
          {create.isPending ? t("app.s2_adding") : t("app.s2_add_entity")}
        </button>
      </div>
    </div>
  );
}
